<template>
  <focus-trap :escapeDeactivates="false" :clickDeactivates="false">
    <div
      class="unit-reference-range-popup"
      :tabindex="`-${layerNumber}`"
      @keyup.esc.stop="handleClose"
    >
      <popup
        :buttons="buttons"
        :content-style="{
          maxHeight: '650px',
          marginTop: '25px',
          padding: '0 30px'
        }"
        :layer-number="layerNumber"
        @cancel="handleClose"
        @click-close-mark="handleClose"
        @decision="inputValues"
      >
        <template v-slot:content>
          <div class="content">
            <div class="title">単位基準値一括変換</div>
            <div class="wrapper">
              <div class="patient">
                <measurement-result-patient-detail
                  data-test="measurement-result-patient-detail"
                  :patient="patient"
                  :run-date="measurementResult.date"
                />
              </div>
              <div class="measurement-result">
                <div class="measurement-result-items-table">
                  <div class="table-header">
                    <div class="table-cell label left">検査項目</div>
                    <div class="table-cell label">
                      <div class="unit-wrapper">
                        <base-check-box
                          data-test="base-check-box bulk-unit"
                          :is-checked="unitChecked"
                          @input="toggleBulkChecked('unitChecked', $event)"
                        />
                        <div class="unit">単位</div>
                      </div>
                    </div>
                    <div class="table-cell label">
                      <div class="range-wrapper">
                        <base-check-box
                          data-test="base-check-box bulk-range"
                          :is-checked="rangeChecked"
                          @input="toggleBulkChecked('rangeChecked', $event)"
                        />
                        <div class="unit">基準値</div>
                      </div>
                    </div>
                    <div class="table-cell label">変換前</div>
                    <div class="table-cell label">変換後</div>
                  </div>
                  <div class="table-body">
                    <div
                      v-for="(item, index) in displayMeasurementResultItems"
                      class="table-row"
                      :key="item.id"
                    >
                      <div class="table-cell left">{{ item.name }}</div>
                      <div class="table-cell">
                        <div v-if="hasUnitAndRange(item)" class="unit-wrapper">
                          <base-check-box
                            :data-test="`base-check-box unit-${index}`"
                            :is-checked="item.unitChecked"
                            @input="toggleChecked(index, 'unitChecked', $event)"
                          />
                          <div class="text">変換</div>
                        </div>
                      </div>
                      <div class="table-cell">
                        <div v-if="hasUnitAndRange(item)" class="range-wrapper">
                          <base-check-box
                            :data-test="`base-check-box range-${index}`"
                            :is-checked="item.rangeChecked"
                            @input="
                              toggleChecked(index, 'rangeChecked', $event)
                            "
                          />
                          <div class="text">変換</div>
                        </div>
                      </div>
                      <div class="table-cell">
                        <div
                          v-if="hasUnitAndRange(item)"
                          class="unit-range-wrapper"
                          data-test="unit-range-wrapper before"
                        >
                          <div class="unit">{{ item.unit }}</div>
                          <div>/</div>
                          <div class="range">{{ toRange(item) }}</div>
                        </div>
                      </div>
                      <div class="table-cell">
                        <div
                          v-if="hasUnitAndRange(item)"
                          class="unit-range-wrapper"
                          data-test="unit-range-wrapper after"
                        >
                          <div
                            class="unit"
                            :data-test="`new-unit-${index}`"
                            :class="{ changed: item.unit !== toNewUnit(item) }"
                          >
                            <span>{{ toNewUnit(item) }}</span>
                          </div>
                          <div>/</div>
                          <div
                            class="range"
                            :data-test="`new-range-${index}`"
                            :class="{
                              changed: toRange(item) !== toNewRange(item)
                            }"
                          >
                            <span>{{ toNewRange(item) }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </popup>
    </div>
  </focus-trap>
</template>

<script>
import BaseCheckBox from '@/components/parts/atoms/BaseCheckBox'
import MeasurementResultPatientDetail from '@/components/parts/molecules/MeasurementResultPatientDetail'
import Popup from '@/components/popups/Popup'
import { FocusTrap } from 'focus-trap-vue'
import { hasUnitAndRange } from '@/utils/measurement_utils'
import { mapGetters } from 'vuex'
import { toItemDefaultValues, toDisplayRange } from '@/utils/measurement_utils'

export default {
  name: 'UnitReferenceRangePopup',

  components: {
    BaseCheckBox,
    FocusTrap,
    MeasurementResultPatientDetail,
    Popup
  },

  props: {
    layerNumber: { type: Number, default: 0 },
    measurementResult: { type: Object, default: () => {} },
    measurementResultItems: { type: Array, default: () => [] },
    patient: { type: Object, default: () => {} }
  },

  data() {
    return {
      buttons: ['閉じる', '一括変換'],
      displayMeasurementResultItems: [],
      rangeChecked: true,
      unitChecked: true
    }
  },

  computed: {
    ...mapGetters({
      getLaboratoryItem: 'laboratoryItems/getLaboratoryItem',
      getLaboratoryReferenceRange:
        'laboratoryReferenceRanges/getLaboratoryReferenceRange',
      getMasterLaboratoryItem: 'master/getMasterLaboratoryItemIndexedByCode'
    })
  },

  created() {
    this.setData()
  },

  methods: {
    getItemDefaultValues(itemCode) {
      const laboratoryItem = this.getLaboratoryItem(
        this.measurementResult.masterLaboratoryInstrumentCode,
        itemCode
      )
      const laboratoryItemRange = this.getLaboratoryReferenceRange(
        this.measurementResult.masterLaboratoryInstrumentCode,
        itemCode,
        this.measurementResult.masterLaboratorySpeciesCode,
        this.patient.birthday,
        this.measurementResult.date
      )
      const { unit, low, high } = toItemDefaultValues(
        laboratoryItem,
        laboratoryItemRange
      )
      return { unit, low, high }
    },
    handleClose() {
      this.$emit('close')
    },
    inputValues() {
      const measurementResultItems = this.measurementResultItems.map((v, i) => {
        const item = this.displayMeasurementResultItems[i]
        const newRange = this.toNewRange(item)
        const [newLow, newHigh] = newRange.split(' – ')
        return {
          ...v,
          unit: this.toNewUnit(item),
          referenceRangeLow: newLow,
          referenceRangeHigh: newHigh
        }
      })
      this.$emit('input', measurementResultItems)
      this.$emit('close')
    },
    setData() {
      this.displayMeasurementResultItems = this.measurementResultItems.map(
        v => {
          const masterLaboratoryItem = this.getMasterLaboratoryItem(
            v.masterLaboratoryItemCode
          )
          const defaultValues = this.getItemDefaultValues(
            v.masterLaboratoryItemCode
          )
          return {
            id: v.id,
            masterLaboratoryItemCode: v.masterLaboratoryItemCode,
            name: masterLaboratoryItem.enName,
            unitChecked: true,
            rangeChecked: true,
            unit: v.unit,
            referenceRangeLow: v.referenceRangeLow,
            referenceRangeHigh: v.referenceRangeHigh,
            defaultUnit: defaultValues.unit,
            defaultLow: defaultValues.low,
            defaultHigh: defaultValues.high
          }
        }
      )
    },
    toggleBulkChecked(key, value) {
      this[key] = value
      this.displayMeasurementResultItems = this.displayMeasurementResultItems.map(
        v => {
          return { ...v, [key]: value }
        }
      )
    },
    toggleChecked(index, key, value) {
      if (!value) this[key] = value
      this.displayMeasurementResultItems[index][key] = value
    },
    toNewRange(item) {
      return item.rangeChecked
        ? toDisplayRange(item.defaultLow, item.defaultHigh)
        : toDisplayRange(item.referenceRangeLow, item.referenceRangeHigh)
    },
    toNewUnit(item) {
      return item.unitChecked ? item.defaultUnit : item.unit
    },
    toRange(item) {
      const { referenceRangeLow, referenceRangeHigh } = item
      return toDisplayRange(referenceRangeLow, referenceRangeHigh)
    },
    hasUnitAndRange(item) {
      return hasUnitAndRange(item.masterLaboratoryItemCode)
    }
  }
}
</script>

<style lang="scss" scoped>
$title-height: 43px;
$patient-detail-height: 70px;

@mixin table-cell {
  padding: 4px 6px;
  font-size: 13px;
  overflow-wrap: anywhere;
  line-break: strict;
  display: flex;
  justify-content: center;
  align-items: center;
  &.label {
    background-color: #{$very-light-shade-orange};
  }
}

@mixin table-row {
  display: grid;
  grid-template-columns: subgrid;
  grid-column: span 5;
  > .table-cell {
    @include table-cell;
    text-align: center;
    &.left {
      text-align: left;
      justify-content: left;
    }
    > .unit-wrapper {
      flex: 1;
      display: flex;
      justify-content: left;
      align-items: center;
      gap: 0 6px;
    }
    > .range-wrapper {
      flex: 1;
      display: flex;
      justify-content: left;
      align-items: center;
      gap: 0 6px;
    }
    > .unit-range-wrapper {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 0 4px;
      > .unit {
        flex: 1;
        display: flex;
        justify-content: right;
        &.changed {
          color: red;
        }
      }
      > .range {
        flex: 1;
        display: flex;
        justify-content: left;
        &.changed {
          color: red;
        }
      }
    }
  }
}

.unit-reference-range-popup {
  position: absolute;
  top: 0;
  left: 0;
  .content {
    box-sizing: border-box;
    width: 850px;
    > .title {
      position: sticky;
      top: 0;
      z-index: 300;
      background: #fff;
      height: $title-height;
      font-size: 16px;
      font-weight: bold;
      color: #{$greyish-brown};
      text-align: left;
    }
    > .wrapper {
      > .patient {
        position: sticky;
        top: $title-height;
        z-index: 200;
        background: #fff;
      }
      > .measurement-result {
        min-height: 200px;
        > .measurement-result-items-table {
          position: relative;
          display: grid;
          grid-template-columns: 200px 90px 90px auto auto;
          > .table-header {
            @include table-row;
          }
          > .table-body {
            display: grid;
            grid-template-columns: subgrid;
            grid-column: span 5;
            > .table-row {
              @include table-row;
              &:nth-child(even) {
                background-color: #f2f2f2;
              }
            }
          }
        }
      }
    }
  }
}
</style>
